import {getDateFromUnixTimestamp, getDayMonthYearFormat, getMonthlyCalendarEndDate, getMonthlyCalendarStartDate, getUnixTimestamp} from '@Utils/date.util';
import type {
  AreaFilterInfo,
  BaseEvent,
  ContentPlannerEntries,
  ContentPlannerFilters,
  ContentPlannerLazyLoadingMap,
  EntryTypeFilter,
  EventEntry,
  PlannerSeenServerResponse,
  ScheduledEntry,
  SocialGlobalEventFilter,
  UserCalendarPreferences,
} from '@Components/content-planner/content-planner.types';
import {
  CalendarEntriesViewType,
  ContentOwnershipTypeFilter,
  EntryType,
  EntryTypeFilterOption,
  EventType,
  PlannerSeenCacheKeyPrefix,
  SocialGlobalEventFilterOption,
} from '@Components/content-planner/content-planner.types';
import type {FiltersHashmap} from '@Components/checkbox-filters/checkbox-filters.types';
import type {EventsFilters, EventVO} from '@Components/events/events.types';
import {getFilteredEvents, isCurrentUserEventCreator} from '@Libraries/events.library';
import type {SocialPost} from '@Components/social-media/post.vo';
import type {EmailCampaign} from '@Components/email-marketing-campaigns/email-marketing-campaigns.types';
import {isSocialMediaPostOwnedByCurrentUser, isSocialMediaPostSharedByTeamMember} from '@Libraries/social-media-post.library';
import {isEmailCampaignOwnedByCurrentUser, isEmailCampaignSharedByTeamMember} from '@Libraries/email-campaigns-library';
import {MY_STUFF_HASHES} from '@Libraries/my-stuff-library';

export const DEFAULT_NUM_ROWS = 6;
export const DEFAULT_EVENT_ID = -1;
export const MAX_EVENT_DESCRIPTION_LENGTH = 255;
export const CONTENT_PLANNER_HASH_CHANGE_EVENT = 'contentPlannerHashChange';

export type Dictionary<T> = Record<string, T>;

export const getEntriesMapKey = (date: Date): string => {
  const dateFrom = getUnixTimestamp(getMonthlyCalendarStartDate(date));
  const dateTo = getUnixTimestamp(getMonthlyCalendarEndDate(DEFAULT_NUM_ROWS, date));

  return dateFrom.toString() + dateTo.toString();
};

export const shouldScheduledEntryBeShown = (entry: ScheduledEntry, entryTypeFilters: EntryTypeFilter): boolean => {
  return (
    (entry.entryType === EntryType.EMAIL && entryTypeFilters[EntryTypeFilterOption.EMAIL]) ||
    (entry.entryType === EntryType.SOCIAL && entryTypeFilters[EntryTypeFilterOption.SOCIAL])
  );
};

export const shouldEventEntryBeShown = (event: EventEntry, filters: ContentPlannerFilters): boolean => {
  return (isInternalEvent(event) && shouldInternalEventBeShown(event, filters)) || (isCustomEvent(event) && filters.entryTypeFilters[EntryTypeFilterOption.USER_EVENT]);
};

export const shouldInternalEventBeShown = (event: EventEntry, filters: ContentPlannerFilters): boolean => {
  const shouldShowSocialEvent = event.type === EventType.SOCIAL_MEDIA && filters.socialGlobalEventFilters[SocialGlobalEventFilterOption.SOCIAL_MEDIA];
  const shouldShowGlobalEvent = event.type === EventType.GLOBAL && filters.socialGlobalEventFilters[SocialGlobalEventFilterOption.GLOBAL];

  if (shouldShowGlobalEvent || shouldShowSocialEvent) {
    return true;
  }

  const doesEventIncludeRegionFromFilter = getSelectedFilterKeys(filters.eventRegionFilters).some((regionId) => {
    return event.regions.includes(regionId);
  });

  if (doesEventIncludeRegionFromFilter) {
    return true;
  }

  return getSelectedFilterKeys(filters.eventReligionFilters).some((religionId) => {
    return event.religions.includes(religionId);
  });
};

export const isDateInCalendar = (calendarMonthDate: Date, targetDate: Date): boolean => {
  return targetDate >= getMonthlyCalendarStartDate(calendarMonthDate) && targetDate <= getMonthlyCalendarEndDate(DEFAULT_NUM_ROWS, calendarMonthDate);
};

export const openIntroDialogForContentPlanner = (): void => {
  window.PMW.showPremiumIntroFeatureDialog(window.PMW.INTRO_FEATURE_NAME_CONTENT_PLANNER);
};

export const openEventsIntroPopupForContentPlanner = (): void => {
  loadContentPlannerDependencies(() => {
    window.PMW.contentPlanner?.openEventsIntroPopup();
  });
};

export const loadContentPlannerDependencies = (onLoad: () => void): void => {
  window.PMW.util.require(typeof window.PMW.contentPlanner === 'undefined', 'contentPlannerDependencies', true, () => {
    onLoad();
  });
};

export const getUserCalendarPreferencesFromState = (filterState: ContentPlannerFilters): UserCalendarPreferences => {
  const {socialGlobalEventFilters, eventRegionFilters, eventReligionFilters} = filterState;

  return {
    showGlobalEvents: socialGlobalEventFilters[SocialGlobalEventFilterOption.GLOBAL],
    showSocialEvents: socialGlobalEventFilters[SocialGlobalEventFilterOption.SOCIAL_MEDIA],
    selectedRegions: getSelectedFilterKeys(eventRegionFilters),
    selectedReligions: getSelectedFilterKeys(eventReligionFilters),
  };
};

export const getIdsFromAreaFiltersList = (areaFilters: AreaFilterInfo[]): string[] => {
  return areaFilters.map((areaFilter) => {
    return areaFilter.id.toString();
  });
};

export const getSelectedFilterKeys = (filter: FiltersHashmap): string[] => {
  return Object.keys(filter).filter((key) => {
    return filter[key];
  });
};

export const hasUserSeenPlanner = async (plannerSeenCacheKeyPrefix: PlannerSeenCacheKeyPrefix): Promise<boolean> => {
  if (!window.PMW.isUserLoggedIn()) {
    return true;
  }

  if (getContentPlannerSeenCache(plannerSeenCacheKeyPrefix)) {
    return true;
  }
  setContentPlannerSeenCache(plannerSeenCacheKeyPrefix);

  const uri = plannerSeenCacheKeyPrefix === PlannerSeenCacheKeyPrefix.EVENTS ? 'hasUserSeenPlannerEvents' : 'hasUserSeenPlanner';
  const data = (await window.PMW.readLocal(`planner/${uri}`)) as PlannerSeenServerResponse;
  data.hasSeen = typeof data.hasSeen === 'string' ? parseInt(data.hasSeen, 10) : data.hasSeen;
  return data.hasSeen === 1;
};

export const hasUserSeenPlannerIntro = async (): Promise<boolean> => {
  return hasUserSeenPlanner(PlannerSeenCacheKeyPrefix.INTRO);
};

export const hasUserSeenPlannerEvents = async (): Promise<boolean> => {
  return hasUserSeenPlanner(PlannerSeenCacheKeyPrefix.EVENTS);
};

export const showEventsIntroPopupIfNeeded = (): void => {
  hasUserSeenPlannerEvents().then(
    (hasSeen) => {
      if (!hasSeen) {
        openEventsIntroPopupForContentPlanner();
      }
    },
    (rejectReason: unknown) => {
      console.error(rejectReason);
    }
  );
};

const getContentPlannerSeenCache = (cacheKeyPrefix: string): boolean => {
  return window.PMW.getCacheItem(getContentPlannerSeenCacheKey(cacheKeyPrefix)) !== null;
};

const setContentPlannerSeenCache = (cacheKeyPrefix: string): void => {
  window.PMW.setCacheItem(getContentPlannerSeenCacheKey(cacheKeyPrefix), 1);
};

const getContentPlannerSeenCacheKey = (cacheKeyPrefix: string): string => {
  return cacheKeyPrefix + (window.PMW.getUserId() as string);
};

export const isCustomEvent = (event: EventEntry): boolean => {
  return event.type === EventType.CUSTOM;
};

export const isInternalEvent = (event: EventEntry): boolean => {
  return event.type !== EventType.CUSTOM;
};

export const isNonLunarEvent = (event: EventEntry): boolean => {
  return isInternalEvent(event) && event.isLunar !== undefined && !event.isLunar;
};

export const getEmptyEntriesObject = (): ContentPlannerEntries => {
  return {
    contentOwnershipFilter: ContentOwnershipTypeFilter.ALL,
    scheduledEntries: [],
    internalEventEntries: [],
    userEventEntries: [],
    emailCampaignEntries: [],
    socialPostEntries: [],
  };
};

export const getEntryTypeFiltersInitialState = (): EntryTypeFilter => {
  return {
    [EntryTypeFilterOption.EMAIL]: true,
    [EntryTypeFilterOption.SOCIAL]: true,
    [EntryTypeFilterOption.USER_EVENT]: true,
  };
};

export const getEventTypeFiltersInitialState = (): SocialGlobalEventFilter => {
  return {
    [SocialGlobalEventFilterOption.GLOBAL]: true,
    [SocialGlobalEventFilterOption.SOCIAL_MEDIA]: true,
  };
};

export const filterScheduledEntriesForDate = (scheduledEntries: ScheduledEntry[], entryTypeFilters: EntryTypeFilter): ScheduledEntry[] => {
  return scheduledEntries.filter((scheduledEntry) => {
    return shouldScheduledEntryBeShown(scheduledEntry, entryTypeFilters);
  });
};

export const getUserEventEntriesForPlannerViewType = (userEventEntries: EventVO[], filters: ContentPlannerFilters): EventVO[] => {
  return filters.entryTypeFilters[EntryTypeFilterOption.USER_EVENT] ? userEventEntries : [];
};

export const filterEventEntriesForDate = (eventEntries: EventEntry[], filters: ContentPlannerFilters): EventEntry[] => {
  return eventEntries.filter((eventEntry) => {
    return shouldEventEntryBeShown(eventEntry, filters);
  });
};

export const getSelectedFilterNames = (filters: FiltersHashmap, filtersSource: AreaFilterInfo[]): string[] => {
  const selectedNames: string[] = [];
  filtersSource.forEach((source) => {
    if (filters[source.id]) {
      selectedNames.push(source.name);
    }
  });

  return selectedNames;
};

export const getFormattedDateForEntryType = (date: Date): string => {
  return getDayMonthYearFormat(date);
};

export const getFormattedDateForEntryTypeFromTimestamp = (timestamp: number): string => {
  return getFormattedDateForEntryType(getDateFromUnixTimestamp(timestamp));
};

export const getEmptyBaseEventEntry = (date = new Date()): BaseEvent => {
  return {
    id: DEFAULT_EVENT_ID,
    timestamp: getUnixTimestamp(date),
    title: '',
    description: '',
  };
};

export const areMoreLazyLoadingEntriesNeeded = (entryTypeLazyLoadData: ContentPlannerLazyLoadingMap): boolean => {
  let entriesNeeded = false;
  Object.keys(entryTypeLazyLoadData).forEach((key: string) => {
    const entryDataForKey = entryTypeLazyLoadData[key];
    if (entryDataForKey.areMoreEntriesNeeded) {
      entriesNeeded = true;
    }
  });
  return entriesNeeded;
};

export const isCalendarViewTypeForContentPlanner = (viewType: CalendarEntriesViewType): boolean => {
  return viewType === CalendarEntriesViewType.PLANNER;
};

export const isUserEventsViewTypeForContentPlanner = (viewType: CalendarEntriesViewType): boolean => {
  return viewType === CalendarEntriesViewType.EVENTS || viewType === CalendarEntriesViewType.EVENTS_SHARED_WITH_ME;
};

export const getFilteredInternalEventsByViewType = (viewType: CalendarEntriesViewType, entries: EventEntry[], filters: ContentPlannerFilters): EventEntry[] => {
  return isCalendarViewTypeForContentPlanner(viewType) ? filterEventEntriesForDate(entries, filters) : entries;
};

export const getFilteredScheduledEntriesByViewType = (viewType: CalendarEntriesViewType, entries: ScheduledEntry[], filters: ContentPlannerFilters): ScheduledEntry[] => {
  return isCalendarViewTypeForContentPlanner(viewType) ? filterScheduledEntriesForDate(entries, filters.entryTypeFilters) : entries;
};

export const getUserEventEntriesByViewType = (
  viewType: CalendarEntriesViewType,
  entries: EventVO[],
  filters: ContentPlannerFilters,
  userEventFilters: EventsFilters
): EventVO[] => {
  const events = getEventEntriesByOwnershipStatus(entries, filters);

  if (isCalendarViewTypeForContentPlanner(viewType)) {
    return getUserEventEntriesForPlannerViewType(events, filters);
  }

  return getFilteredEvents(events, userEventFilters);
};

const getEventEntriesByOwnershipStatus = (entries: EventVO[], filters: ContentPlannerFilters): EventVO[] => {
  switch (filters.contentOwnerFilters) {
    case ContentOwnershipTypeFilter.ALL:
      return entries;
    case ContentOwnershipTypeFilter.OWN: {
      return entries.filter((event) => isCurrentUserEventCreator(event));
    }
    case ContentOwnershipTypeFilter.TEAM: {
      return entries.filter((event) => !isCurrentUserEventCreator(event) && event.isShared);
    }
    default:
      return [];
  }
};

export const getSocialPostEntriesByViewType = (viewtype: CalendarEntriesViewType, entries: SocialPost[], filters: ContentPlannerFilters): SocialPost[] => {
  const socialPosts = getSocialPostsByOwnershipStatus(entries, filters);

  if (isCalendarViewTypeForContentPlanner(viewtype)) {
    return filters.entryTypeFilters[EntryTypeFilterOption.SOCIAL] ? socialPosts : [];
  }

  return socialPosts;
};

export const getEmailCampaignEntriesByViewType = (viewtype: CalendarEntriesViewType, entries: EmailCampaign[], filters: ContentPlannerFilters): EmailCampaign[] => {
  const emailCampaigns = getEmailCampaignsByOwnershipStatus(entries, filters);

  if (isCalendarViewTypeForContentPlanner(viewtype)) {
    return filters.entryTypeFilters[EntryTypeFilterOption.EMAIL] ? emailCampaigns : [];
  }

  return emailCampaigns;
};

export const getEmailCampaignsByOwnershipStatus = (entries: EmailCampaign[], filters: ContentPlannerFilters): EmailCampaign[] => {
  switch (filters.contentOwnerFilters) {
    case ContentOwnershipTypeFilter.ALL:
      return entries;
    case ContentOwnershipTypeFilter.OWN: {
      return entries.filter((campaign) => isEmailCampaignOwnedByCurrentUser(campaign));
    }
    case ContentOwnershipTypeFilter.TEAM: {
      return entries.filter((campaign) => isEmailCampaignSharedByTeamMember(campaign));
    }
    default:
      return [];
  }
};

export const getSocialPostsByOwnershipStatus = (entries: SocialPost[], filters: ContentPlannerFilters): SocialPost[] => {
  switch (filters.contentOwnerFilters) {
    case ContentOwnershipTypeFilter.ALL:
      return entries;
    case ContentOwnershipTypeFilter.OWN: {
      return entries.filter((post) => isSocialMediaPostOwnedByCurrentUser(post));
    }
    case ContentOwnershipTypeFilter.TEAM: {
      return entries.filter((post) => isSocialMediaPostSharedByTeamMember(post));
    }
    default:
      return [];
  }
};

export const triggerInternalEventTemplatesDialogOpening = (internalEvent: EventEntry): void => {
  window.PMW.setCacheItem('current_event_timestamp', internalEvent.timestamp);
  history.pushState(null, '', `${MY_STUFF_HASHES.contentPlanner}/${internalEvent.eventHashedId}`);
  window.dispatchEvent(new Event(CONTENT_PLANNER_HASH_CHANGE_EVENT));
};
